import type { TreeInput } from '../nodes'
import { isSimilarTitle } from './similarTitle'

export const MAX_OWN_TASKS = 20
export const MAX_OWN_TASK_TITLE = 200

type PlanTask = TreeInput['milestones'][number]['targets'][number]['tasks'][number]

const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')

export function cleanOwnTasks(titles: readonly string[] | undefined): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of titles ?? []) {
    const title = raw.replace(/\s+/g, ' ').trim().slice(0, MAX_OWN_TASK_TITLE)
    const key = norm(title)
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(title)
    if (out.length === MAX_OWN_TASKS) break
  }
  return out
}

// Each of the user's own tasks lands in the plan exactly once, under its own title and
// flagged `own`; the model's rewordings and repeats of them are dropped, and any it left
// out go into the first target.
export function placeOwnTasks<T extends TreeInput>(tree: T, own: readonly string[]): T {
  const tasks = tree.milestones.flatMap((m) => m.targets.flatMap((t) => t.tasks))
  const owner = new Map<PlanTask, number>()
  const claimed = new Set<number>()
  const claim = (task: PlanTask, match: (o: string) => boolean) => {
    const i = own.findIndex((o, j) => !claimed.has(j) && match(o))
    if (i === -1) return
    claimed.add(i)
    owner.set(task, i)
  }
  for (const task of tasks) {
    claim(task, (o) => norm(o) === norm(task.title))
    if (!owner.has(task) && task.own) claim(task, (o) => isSimilarTitle(o, task.title))
  }
  for (const task of tasks) if (!owner.has(task)) claim(task, (o) => isSimilarTitle(o, task.title))

  const keep = (task: PlanTask) =>
    owner.has(task) ||
    !own.some((o) => norm(o) === norm(task.title) || isSimilarTitle(o, task.title))
  const milestones = tree.milestones
    .map((m) => ({
      ...m,
      targets: m.targets
        .map((t) => ({
          ...t,
          tasks: t.tasks.filter(keep).map((task) => {
            const { own: _own, ...rest } = task
            const i = owner.get(task)
            return i === undefined ? rest : { ...rest, title: own[i], own: true }
          }),
        }))
        .filter((t) => t.tasks.length > 0),
    }))
    .filter((m) => m.targets.length > 0)

  const missing = own.filter((_, i) => !claimed.has(i))
  if (missing.length && milestones[0]) {
    const target = milestones[0].targets[0]
    target.tasks = [
      ...missing.map((title) => ({ title, effort: 'medium' as const, own: true })),
      ...target.tasks,
    ]
  }
  return { ...tree, milestones }
}
